import React from 'react';
import {Text, View, StyleSheet as PDFStyleSheet} from '@react-pdf/renderer';
import {consolidateSchoolYears} from 'shared/utils';
import Activity from 'src/components/Resume/activity';
import {Activity as ActivityData, Award, WorkExperience} from 'src/types/user';
import {CommunityServiceEvent} from 'src/types/store';

type SectionActivity = ActivityData | Award | WorkExperience | CommunityServiceEvent;

interface FormattedActivity {
  name: string;
  description: string;
  date: string;
}

interface Props {
  name: string;
  activities: SectionActivity[];
  blackAndWhite?: boolean;
}

const ResumeSection = ({name, activities, blackAndWhite}: Props) => {
  const styles = PDFStyleSheet.create({
    sectionHeader: {
      marginTop: 16,
      fontSize: 16,
      color: blackAndWhite ? 'black' : '#366aff',
    },
    line: {
      marginBottom: 4,
      height: 1,
      backgroundColor: 'black',
    },
  });

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Format the properties name, description, and date based on different sections.
  const formattedActivities: FormattedActivity[] = activities.map((activity) => {
    const activityObj: Partial<FormattedActivity> = {};

    // Add name formatting.
    if (name === 'Community Service' || name === 'Work Experience') {
      activityObj.name = (activity as WorkExperience | CommunityServiceEvent).place;
    } else {
      activityObj.name = (activity as ActivityData | Award).name;
    }

    // Add description formatting.
    if (name === 'Work Experience') {
      activityObj.description = (activity as WorkExperience).responsibilities;
    } else {
      activityObj.description = (
        activity as ActivityData | Award | CommunityServiceEvent
      ).description;
    }

    // Add date formatting.
    if (name === 'Community Service' || name === 'Work Experience') {
      const {startMonth, startYear, endMonth, endYear} = activity as
        | WorkExperience
        | CommunityServiceEvent;
      activityObj.date = months[startMonth] + ' ' + startYear;
      if (endMonth && endYear) {
        activityObj.date += ' - ' + months[Number(endMonth)] + ' ' + endYear;
      } else if ((activity as WorkExperience).currentWorkplace) {
        activityObj.date += ' - Present';
      }
    } else if (name === 'Honors and Awards') {
      const {month, year} = activity as Award;
      activityObj.date = months[month] + ' ' + year;
    } else if (name === 'Extracurriculars') {
      activityObj.date = consolidateSchoolYears((activity as ActivityData).schoolYears).join(', ');
    }

    return activityObj as FormattedActivity;
  });

  // Create list of Activity elements.
  const activityElements = formattedActivities.map((activity) => (
    <Activity
      name={activity.name}
      key={activity.name}
      description={activity.description}
      date={activity.date}
    />
  ));

  return (
    <View>
      <Text style={styles.sectionHeader}>{name}</Text>
      {blackAndWhite && <View style={styles.line} />}
      {activityElements}
    </View>
  );
};

export default ResumeSection;
